import { useEffect, useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { Upload, Trash2, ArrowLeft, PlayCircle, CheckCircle2, Volume2, ExternalLink } from "lucide-react";

/**
 * /admin/tutorials — Mike's walk-through video manager.
 * Lists every page slot that supports a tutorial video. Drag-drop or click
 * to upload an MP4 from ScreenPal. Backed by /api/admin/tutorials.
 */
export default function AdminTutorialsPage() {
  const [me, setMe] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch { setMe(null); }
    })();
  }, []);

  const loadSlots = async () => {
    try {
      const { data } = await api.get("/admin/tutorials");
      setSlots(data?.slots || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load tutorial slots");
    }
  };

  useEffect(() => {
    if (me?.is_admin) loadSlots();
  }, [me]);

  if (me === undefined) return <div className="min-h-screen flex items-center justify-center text-foreground">Loading…</div>;
  if (!me) return <Navigate to="/login" replace />;
  if (!me.is_admin) return <Navigate to="/dashboard" replace />;

  // Pre-resolved routes are stored on the slot itself by loadSlots(), so
  // both the Open Page link and the Record button can use slot.route directly.

  const handleUpload = async (pageKey, file) => {
    if (!file) return;
    setBusyKey(pageKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/tutorials/${pageKey}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`★ Video uploaded for ${pageKey}`);
      await loadSlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusyKey(null);
    }
  };

  const handleDelete = async (pageKey) => {
    if (!window.confirm("Remove the walk-through video from this page?")) return;
    setBusyKey(pageKey);
    try {
      await api.delete(`/admin/tutorials/${pageKey}`);
      toast.success("Removed");
      await loadSlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }} data-testid="admin-tutorials-page">
      <SiteNav />

      <section className="border-b-2"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)",
                        borderColor: "#2E9F3A" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14">
          <Link to="/dashboard"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] hover:opacity-80 mb-3"
                style={{ color: "#0F172A" }}>
            <ArrowLeft className="w-4 h-4"/> My Office
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] tracking-[0.3em] uppercase font-bold mb-3"
               style={{ background: "#EAF6FC", color: "#FFFFFF" }}>
            <Volume2 className="w-3.5 h-3.5"/> ★ TUTORIAL VIDEOS · ADMIN ONLY
          </div>
          <h1 className="font-serif-display uppercase leading-[0.98]"
              style={{ color: "#0F172A", textWrap: "balance",
                       fontSize: "clamp(2rem, 5vw, 3.6rem)" }}>
            Your Walk-Through<br/>
            <span style={{ color: "#2E9F3A" }}>Video Library.</span>
          </h1>
          <p className="mt-4 text-base md:text-lg max-w-2xl" style={{ color: "#0F172A" }}>
            Click <strong style={{ color: "#C9202E" }}>● Record Now</strong> next to any page.
            Your browser captures your screen + voice + a big yellow cursor ring. Save when done —
            it goes live on that page right then. <strong>No leaving Faitharama.</strong>
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14">
        {/* Other admin tools — quick-jump tiles */}
        <div className="mb-8" data-testid="admin-other-tools">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold mb-2" style={{ color: "#2E9F3A" }}>
            ★ OTHER ADMIN TOOLS
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link to="/admin/with-faith-chapters"
                  data-testid="link-edit-chapters-btn"
                  className="rounded-xl border-2 bg-white p-4 flex items-center gap-3 hover:shadow-md transition"
                  style={{ borderColor: "#0F172A" }}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                   style={{ background: "#EAF6FC", color: "#E8B539" }}>
                <PlayCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold uppercase tracking-wider text-sm" style={{ color: "#0F172A" }}>
                  Edit Book Chapters
                </div>
                <div className="text-xs" style={{ color: "#3A2F23" }}>
                  Titles + one-liners on the /with-faith Sketch Wall.
                </div>
              </div>
            </Link>

            <Link to="/admin/book-cover-orders"
                  data-testid="link-cover-orders-btn"
                  className="rounded-xl border-2 bg-white p-4 flex items-center gap-3 hover:shadow-md transition"
                  style={{ borderColor: "#0F172A" }}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                   style={{ background: "#EAF6FC", color: "#E8B539" }}>
                <PlayCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold uppercase tracking-wider text-sm" style={{ color: "#0F172A" }}>
                  Cover Order Queue
                </div>
                <div className="text-xs" style={{ color: "#3A2F23" }}>
                  $25 designed-cover orders waiting for you.
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* ★ WELCOME VIDEO SLOT — paste a YouTube link, it lands at the top
            of every member's back office. Fastest way to give new members
            a personal hello. */}
        <WelcomeVideoAdmin />

        <div className="rounded-xl p-5 md:p-6 mb-6"
             style={{ background: "#FFFFFF", border: "2px solid #2E9F3A" }}
             data-testid="recorder-howto">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold mb-2" style={{ color: "#C9202E" }}>
            ★ HOW IT WORKS · USE SCREENPAL TO RECORD
          </div>
          <ol className="space-y-2 text-base md:text-lg" style={{ color: "#0F172A" }}>
            <li><strong>1.</strong> Click <strong>Open Page</strong> next to the page you want to walk through — it opens in a new window so you can rehearse.</li>
            <li><strong>2.</strong> Open <a href="https://screenpal.com/screen-recorder" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "#0F172A" }}>screenpal.com</a>, hit record, talk and click your way through.</li>
            <li><strong>3.</strong> Save the MP4 to your desktop.</li>
            <li><strong>4.</strong> Come back here, click <strong>Upload MP4</strong> next to the right page — it goes live for members instantly.</li>
          </ol>
        </div>

        <div className="space-y-4" data-testid="tutorial-slots">
          {slots.map((slot) => {
            const hasVideo = !!slot.src;
            const isBusy = busyKey === slot.page_key;
            return (
              <div key={slot.page_key}
                   data-testid={`slot-${slot.page_key}`}
                   className="rounded-xl p-5 md:p-6 grid md:grid-cols-[1fr_auto] gap-4 items-center"
                   style={{ background: "#FFFFFF",
                            border: `2px solid ${hasVideo ? "#2E9F3A" : "#2E9F3A"}`,
                            boxShadow: "0 4px 14px rgba(201, 162, 39, 0.10)" }}>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold"
                         style={{ color: "#0F172A" }}>
                      {slot.page_key}
                    </div>
                    {hasVideo && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-bold"
                            style={{ background: "#2E9F3A", color: "#FFFFFF" }}>
                        <CheckCircle2 className="w-3 h-3"/> LIVE
                      </span>
                    )}
                  </div>
                  <div className="font-serif-display text-xl md:text-2xl leading-tight" style={{ color: "#0F172A" }}>
                    {slot.label}
                  </div>
                  <div className="text-sm font-mono mt-1" style={{ color: "#3A2F23" }}>
                    {slot.route}
                  </div>
                  {hasVideo && (
                    <div className="mt-2 text-xs" style={{ color: "#3A2F23" }}>
                      ★ {slot.filename || slot.src.split("/").pop()}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 items-center justify-end">
                  <a href={slot.route} target="_blank" rel="noopener noreferrer"
                     data-testid={`slot-${slot.page_key}-open`}
                     title="Open this page in a new window — useful for picking what to record in ScreenPal"
                     className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-[0.14em] hover:scale-[1.03] transition shadow"
                     style={{ background: "#2E9F3A", color: "#FFFFFF" }}>
                    <ExternalLink className="w-4 h-4"/> Open Page
                  </a>
                  {hasVideo && (
                    <a href={slot.src} target="_blank" rel="noopener noreferrer"
                       data-testid={`slot-${slot.page_key}-preview`}
                       className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-[0.14em] hover:scale-[1.03] transition"
                       style={{ background: "#FFFFFF", color: "#0F172A", border: "2px solid #0F172A" }}>
                      <PlayCircle className="w-4 h-4"/> Preview
                    </a>
                  )}
                  <UploadButton pageKey={slot.page_key} hasVideo={hasVideo} isBusy={isBusy}
                                onUpload={(f) => handleUpload(slot.page_key, f)}/>
                  {hasVideo && (
                    <button onClick={() => handleDelete(slot.page_key)}
                            data-testid={`slot-${slot.page_key}-delete`}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-[0.14em] hover:scale-[1.03] transition disabled:opacity-50"
                            style={{ background: "#FFFFFF", color: "#C9202E", border: "2px solid #C9202E" }}>
                      <Trash2 className="w-4 h-4"/> Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {slots.length === 0 && (
          <div className="text-center py-12 text-foreground/60">Loading slots…</div>
        )}
      </section>

      <SiteFooter />
    </div>
  );
}

function UploadButton({ pageKey, hasVideo, isBusy, onUpload }) {
  const inputRef = useRef(null);
  return (
    <>
      <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
             className="hidden"
             data-testid={`slot-${pageKey}-file`}
             onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}/>
      <button onClick={() => inputRef.current?.click()}
              disabled={isBusy}
              data-testid={`slot-${pageKey}-upload`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-[0.14em] shadow hover:scale-[1.03] transition disabled:opacity-50"
              style={{ background: hasVideo ? "#FFFFFF" : "#2E9F3A",
                       color: hasVideo ? "#0F172A" : "#FFFFFF",
                       border: hasVideo ? "2px solid #2E9F3A" : "2px solid #2E9F3A" }}>
        <Upload className="w-4 h-4"/> {isBusy ? "Uploading…" : (hasVideo ? "Replace" : "Upload MP4")}
      </button>
    </>
  );
}

/* ============================================================================
   ★ WELCOME VIDEO ADMIN — paste a YouTube link, it goes on every member's
   back office. The simplest possible CMS: one input, one save button.
   ============================================================================ */
function WelcomeVideoAdmin() {
  const [v, setV] = useState({ youtube: "", title: "", video_url: "" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [previewId, setPreviewId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/welcome-video");
        const yid = data?.youtube_id || "";
        setV({
          youtube: yid ? `https://www.youtube.com/watch?v=${yid}` : "",
          title: data?.title || "",
          video_url: data?.video_url || "",
        });
        setPreviewId(yid);
      } catch {/* fresh start */}
      finally { setLoaded(true); }
    })();
  }, []);

  // Pull an 11-char YouTube ID out of any common URL or paste
  const extractId = (s) => {
    const t = (s || "").trim();
    const m = t.match(/(?:youtu\.be\/|v=|\/shorts\/|\/embed\/)([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    if (/^[A-Za-z0-9_-]{11}$/.test(t)) return t;
    return "";
  };
  const liveId = extractId(v.youtube);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/welcome-video", {
        youtube: v.youtube,
        video_url: v.video_url,
        title: v.title,
      });
      setPreviewId(r.data?.youtube_id || "");
      toast.success("Welcome video saved — every member sees it now.");
    } catch (err) {
      toast.error(`Save failed: ${err.response?.data?.detail || err.message}`);
    } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the Welcome Video from every member's back office?")) return;
    setSaving(true);
    try {
      await api.put("/admin/welcome-video", { youtube: "", video_url: "", title: "" });
      setV({ youtube: "", title: "", video_url: "" });
      setPreviewId("");
      toast.success("Welcome video removed.");
    } catch (err) {
      toast.error(`Remove failed: ${err.response?.data?.detail || err.message}`);
    } finally { setSaving(false); }
  };

  if (!loaded) return null;

  return (
    <div className="rounded-xl p-5 md:p-6 mb-6"
         style={{ background: "#FFFFFF", border: "3px solid #0F172A",
                  boxShadow: "0 4px 14px rgba(22, 36, 74, 0.15)" }}
         data-testid="welcome-video-admin">
      <div className="text-[10px] tracking-[0.3em] uppercase font-bold mb-1" style={{ color: "#2E9F3A" }}>
        ★ STEP 1 · WELCOME VIDEO (TOP OF BACK OFFICE)
      </div>
      <div className="font-serif-display text-2xl md:text-3xl uppercase leading-tight mb-2" style={{ color: "#0F172A" }}>
        Paste Your YouTube Link.
      </div>
      <p className="text-sm mb-4" style={{ color: "#3A2F23" }}>
        Record on Screen Pal → Upload to YouTube as <strong>Unlisted</strong> →
        Paste the link below → Hit Save. Every member sees it the second they sign in.
      </p>

      <label className="block text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: "#0F172A" }}>
        YouTube Link
      </label>
      <input type="url"
             value={v.youtube}
             onChange={(e) => setV({ ...v, youtube: e.target.value })}
             placeholder="https://www.youtube.com/watch?v=..."
             data-testid="welcome-video-youtube-input"
             className="w-full px-4 py-3 rounded-md border-2 bg-white text-base font-mono outline-none focus:ring-2 focus:ring-[#2E9F3A]"
             style={{ borderColor: "#2E9F3A" }}/>
      <div className="mt-1 text-[11px]" style={{ color: liveId ? "#2E9F3A" : "#9F2727" }}>
        {liveId ? `✓ Detected video ID: ${liveId}` : (v.youtube ? "Can't read that link — paste a YouTube URL" : "")}
      </div>

      <label className="block text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5 mt-4" style={{ color: "#0F172A" }}>
        Title shown next to the video (optional)
      </label>
      <input type="text"
             value={v.title}
             onChange={(e) => setV({ ...v, title: e.target.value })}
             placeholder="Mike's Back-Office Tour."
             data-testid="welcome-video-title-input"
             maxLength={80}
             className="w-full px-4 py-3 rounded-md border-2 bg-white text-base outline-none focus:ring-2 focus:ring-[#2E9F3A]"
             style={{ borderColor: "#2E9F3A" }}/>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={save} disabled={saving || (!liveId && !v.video_url)}
                data-testid="welcome-video-save"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md text-xs font-bold uppercase tracking-[0.18em] shadow text-white disabled:opacity-50 hover:scale-[1.03] transition"
                style={{ background: "#2E9F3A" }}>
          <CheckCircle2 className="w-4 h-4"/> {saving ? "Saving…" : "Save Welcome Video"}
        </button>
        {(previewId || v.video_url) && (
          <button onClick={clear} disabled={saving}
                  data-testid="welcome-video-clear"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-xs font-bold uppercase tracking-[0.18em] shadow disabled:opacity-50"
                  style={{ background: "#FFFFFF", color: "#9F2727", border: "2px solid #9F2727" }}>
            <Trash2 className="w-4 h-4"/> Remove Video
          </button>
        )}
      </div>

      {/* Live preview so Mike can sanity-check before saving */}
      {liveId && (
        <div className="mt-5 rounded-lg overflow-hidden border-2"
             style={{ borderColor: "#2E9F3A" }} data-testid="welcome-video-preview">
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold px-3 py-1.5"
               style={{ background: "#EAF6FC", color: "#2E9F3A" }}>
            ★ Preview · what members will see
          </div>
          <div className="aspect-video bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${liveId}?rel=0&modestbranding=1`}
              title="Welcome Video Preview"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-0"/>
          </div>
        </div>
      )}
    </div>
  );
}

